<script lang="ts">
    import AddGame from "@frontend/Components/AddGame.svelte";
    import EditGameForm from "@frontend/Components/EditGameForm.svelte";
    import Modal from "@frontend/Components/Modal.svelte";
    import { service } from "@frontend/services";
    import { GetCachedToken, me } from "@frontend/user";
    import { push } from "svelte-spa-router";
    import { get } from "svelte/store";
    import { groups } from "@frontend/grouplist";
    import AddGroup from "@frontend/Components/AddGroup.svelte";
    import EditGroupForm from "@frontend/Components/EditGroupForm.svelte";
    import Groups from "./Groups.svelte";
    import { AuthenticatedRequestOfDeleteGroupDTO, GroupDTO } from "@frontend/walletapi";
    import { RefreshGroups } from "@frontend/grouplist";
    import Pagination from "@frontend/Components/Pagination.svelte";
    import { onMount } from "svelte";
    import { translate, currentLanguage } from "../Translator";

    type EditAction = {
      action: "edit",
      groupid: number;
    };

    type AddAction = {
      action: "add"
    };

    type RouteParams =
      EditAction |
      AddAction |
      undefined;
    
    export let params: RouteParams = undefined;

    let selectedGroup: GroupDTO | undefined = undefined;
    $: if (params) {
      if (params.action === "edit") {
        // Convertir a número para la comparación
        const groupId = Number(params.groupid);
        selectedGroup = $groups.find(g => g.Id === groupId);
      }
    }

    // Función para verificar si un grupo es parte de mi jerarquía
    function isGroupInMyHierarchy(group: GroupDTO): boolean {
      // Si soy Owner o SuperAdmin (grupo 1), veo todos los grupos
      if ($me?.Role === 'Owner') {
        return true;
      }

      // Si no hay grupo de usuario definido
      if (!$me?.GroupId) {
        return false;
      }

      const myGroupId = $me.GroupId;

      // 1. Es mi grupo
      if (group.Id === myGroupId) {
        return true;
      }

      // 2. Es hijo directo de mi grupo
      if (group.ParentId === myGroupId) {
        return true;
      }

      // 3. Es descendiente (nieto o más profundo)
      let currentGroup: GroupDTO | undefined = group;
      while (currentGroup?.ParentId) {
        // Si encontramos que algún ancestro es nuestro grupo, incluimos el grupo
        if (currentGroup.ParentId === myGroupId) {
          return true;
        }

        // Buscar el siguiente grupo padre
        currentGroup = $groups.find(g => g.Id === currentGroup?.ParentId);
      }

      return false;
    }

    // Función para verificar si un grupo es el grupo actual del usuario
    function isCurrentUserGroup(group: GroupDTO): boolean {
      if (!$me?.GroupId) return false;
      return group.Id === $me.GroupId;
    }

    // Función para organizar grupos en estructura jerárquica
    function organizeGroupsHierarchy(groups: GroupDTO[]): Array<GroupDTO & { children: Array<GroupDTO & { children: any[] }> }> {
      const groupMap = new Map<string, GroupDTO & { children: Array<GroupDTO & { children: any[] }> }>();
      const rootGroups: Array<GroupDTO & { children: Array<GroupDTO & { children: any[] }> }> = [];

      // Primero, crear un mapa de todos los grupos
      groups.forEach(group => {
        groupMap.set(group.Id.toString(), {
          ...group,
          children: []
        });
      });

      // Luego, construir la jerarquía
      groups.forEach(group => {
        const groupWithChildren = groupMap.get(group.Id.toString());
        if (group.ParentId) {
          const parent = groupMap.get(group.ParentId.toString());
          if (parent && groupWithChildren) {
            parent.children.push(groupWithChildren);
          }
        } else {
          if (groupWithChildren) rootGroups.push(groupWithChildren);
        }
      });

      return rootGroups;
    }

    // Filtrar y organizar grupos jerárquicamente
    $: hierarchicalGroups = organizeGroupsHierarchy(
      $groups.filter(isGroupInMyHierarchy)
    );

    // Función recursiva para renderizar grupos
    function renderGroup(group: GroupDTO & { children: Array<GroupDTO & { children: any[] }> }, level = 0): Array<GroupDTO & { children: Array<GroupDTO & { children: any[] }> } & { level: number }> {
      const result: Array<GroupDTO & { children: Array<GroupDTO & { children: any[] }> } & { level: number }> = [
        { ...group, level }
      ];
      group.children
        .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''))
        .forEach((child) => {
          result.push(...renderGroup(child, level + 1));
        });
      return result;
    }

    // Lista plana de grupos con su nivel de profundidad
    $: flattenedGroups = hierarchicalGroups
      .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''))
      .map(group => renderGroup(group))
      .reduce((acc, val) => acc.concat(val), []);
    let currentPage = 1;
    const pageSize = 20;
    $: visibleGroups = flattenedGroups.slice((currentPage - 1) * pageSize, currentPage * pageSize);


    function dismiss() {
      push("/GroupList");
    }

    let errorMessage = '';
    let successMessage = '';

    let showDeleteModal = false;
    let groupToDelete: GroupDTO | null = null;

    function openDeleteModal(group: GroupDTO) {
        groupToDelete = group;
        showDeleteModal = true;
    }

    function closeDeleteModal() {
        groupToDelete = null;
        showDeleteModal = false;
    }

    async function confirmDeleteGroup() {
        if (groupToDelete) {
            await DeleteGroup(groupToDelete);
            closeDeleteModal();
        }
    }

    async function DeleteGroup(group: GroupDTO) {
        try {
            const requestPayload: AuthenticatedRequestOfDeleteGroupDTO = {
                Token: GetCachedToken(),
                Data: {
                    Id: group.Id
                }
            };

            const result = await service.walletapi.deleteGroup(requestPayload);

            if (result.Success) {
                errorMessage = '';
                successMessage = result.Message || 'Group successfully eliminated.';
                setTimeout(() => {
                    successMessage = '';
                }, 3000);
                await RefreshGroups();
            } else {
                successMessage = '';
                errorMessage = result.ErrorMessage || 'This group cannot be deleted because it has related users.';
                setTimeout(() => {
                    errorMessage = '';
                }, 3000);
            }
        } catch (error) {
            successMessage = '';
            errorMessage = 'The group could not be deleted. Please try again.';
            setTimeout(() => {
                errorMessage = '';
            }, 3000);
        }
    }

    onMount(async () => {
      if (!$me) {
        push("/user/Login");
        return;
      }
      await RefreshGroups();
    });
  </script>

  <style>
    .group-row {
      transition: background-color 0.2s;
    }
    .group-row:hover {
      background-color: #f5f5f5 !important;
    }
    .group-row.current-user-group {
      background-color: #e3f2fd !important;
      border-left: 4px solid #2196f3;
    }
    .group-row.current-user-group:hover {
      background-color: #bbdefb !important;
    }
    .group-name {
      display: flex;
      align-items: center;
    }
    .current-user-badge {
      background-color: #2196f3;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.75em;
      margin-left: 8px;
      font-weight: bold;
    }
    .floating-error {
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      width: 60%;
      max-width: 600px;
      text-align: center;
      pointer-events: none;
    }
    .floating-success {
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      width: 60%;
      max-width: 600px;
      text-align: center;
      pointer-events: none;
    }
    .button-separator {
      display: inline-block;
      width: 1px;
      height: 24px;
      background: #ccc;
      margin: 0 8px;
      vertical-align: middle;
    }
  </style>
  
  {#if params?.action == "add"}
    <Modal dismiss={dismiss}>
      <AddGroup {dismiss}/>
    </Modal>
  {/if}

  {#if params?.action == "edit"}
    <Modal dismiss={dismiss}>
      {#if !selectedGroup}
        <div class="ui negative message">
          <div class="header">Error</div>
          <p>Group not found</p>
        </div>
      {:else}
        <EditGroupForm group={selectedGroup} {dismiss}/>
      {/if}
    </Modal>
  {/if}

  {#if showDeleteModal}
    <Modal dismiss={closeDeleteModal}>
      <div class="ui header">Confirm</div>
      <div>{$translate ('Are you sure you want to delete the group')} <b>{groupToDelete?.Name}</b>?</div>
      <div style="margin-top: 2em; display: flex; gap: 1em; justify-content: flex-end;">
        <button class="ui button" on:click={closeDeleteModal}>Cancel</button>
        <button class="ui red button" on:click={confirmDeleteGroup}>Delete</button>
      </div>
    </Modal>
  {/if}

  <h1 class="ui centered dividing header"> {$translate('List of Groups')} ({flattenedGroups.length ?? ""})</h1>

  <a class="ui positive button" href="#/GroupList/add">
    <i class="plus icon"></i>
   {$translate('Add Group')}
  </a>

  <div class="table-container" style="position: relative;">
    {#if errorMessage}
      <div class="ui negative message floating-error">
        <div class="header">Error</div>
        <p>{errorMessage}</p>
      </div>
    {/if}
    {#if successMessage}
      <div class="ui positive message floating-success">
        <div class="header">Éxito</div>
        <p>{successMessage}</p>
      </div>
    {/if}

    <table class="playerlist ui celled unstackable striped very compact unselectable table">
      <thead>
        <tr>
          <th style="width: 0px">#</th>
          <th>{$translate('Name')}</th>
          <th> {$translate('Parent-id')}</th>
          <th style="width: 0px">Options</th>
        </tr>
      </thead>
      <tbody>
        {#each visibleGroups as group}
          <tr class="group-row" class:current-user-group={isCurrentUserGroup(group)} style="background-color: {group.level % 2 === 0 ? '#ffffff' : '#f9fafb'}">
            <td class="numeric">{group.Id}</td>
            <td>
              <div class="group-name" style="padding-left: {group.level * 20}px">
                {#if group.level > 0}└─ {/if}{group.Name}
                {#if isCurrentUserGroup(group)}
                  <span class="current-user-badge">Mi Grupo</span>
                {/if}
              </div>
            </td>
            <td>
              {#if group.ParentId !== undefined && group.ParentId !== null}
                {@const parentGroup = $groups.find(g => g.Id === group.ParentId)}
                {parentGroup ? parentGroup.Name : String(group.ParentId)}
              {:else}
                (sin padre)
              {/if}
            </td>
            <td>
              <div class="ui icon buttons">
                <a href="#/GroupList/edit/{group.Id}" class="ui button">
                  <i class="edit icon"></i>
                </a>
                <span class="button-separator"></span>
                <button class="ui red icon button" class:enabled={group.Id} on:click={() => openDeleteModal(group)}>
                  <i class="trash icon"></i>
                </button>
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
    <Pagination page={currentPage} totalItems={flattenedGroups.length} {pageSize} on:change={(event) => currentPage = event.detail} />
  </div>
